import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getFacturesExternes,
  getFacturesInternes,
  getPrestataires,
  getConsultants,
  getTemps,
  getCharges,
  getConsultantConfig
} from '../../services/airtable';
import { sortFactures, filterFactures } from '../../utils/sortFactures';
import { getAvailableYearsFromTemps } from '../../utils/yearUtils';
import { useRefacturationTotal } from '../tmp/useRefacturationTotal';
import { useCACalculations } from '../pl/useCACalculations';

/**
 * Hook pour gérer les données de facturation
 *
 * Extrait de FacturationTable.js pour améliorer la maintenabilité
 * Gère: chargement des données, transformations, filtrage, tri
 *
 * @param {Object} params
 * @param {Object} params.state - État depuis useFacturationState
 * @param {Function} params.dispatch - Dispatch depuis useFacturationState
 * @param {Object} params.ACTIONS - Actions depuis useFacturationState
 * @param {number} params.filterAnnee - Année de filtre (depuis YearContext)
 * @returns {Object} Données transformées et fonctions
 */
export function useFacturationData({ state, dispatch, ACTIONS, filterAnnee }) {
  const {
    activeTab,
    facturesExternes,
    facturesInternes,
    prestataires,
    consultants,
    filterMois,
    filterTrimestre,
    filterStatut,
    filterPrestataire,
    filterConsultant,
    hidePaid,
    sortColumn,
    sortDirection
  } = state;

  // État local pour les données additionnelles
  const [tempsData, setTempsData] = useState([]);
  const [chargesData, setChargesData] = useState([]);
  const [consultantConfig, setConsultantConfig] = useState({});

  /**
   * Chargement des données depuis Airtable
   */
  const loadData = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const [externes, internes, prestData, consultData, temps, charges, configData] = await Promise.all([
        getFacturesExternes(),
        getFacturesInternes(),
        getPrestataires(),
        getConsultants(),
        getTemps(),
        getCharges(),
        getConsultantConfig()
      ]);

      dispatch({ type: ACTIONS.SET_FACTURES_EXTERNES, payload: Array.isArray(externes) ? externes : [] });
      dispatch({ type: ACTIONS.SET_FACTURES_INTERNES, payload: Array.isArray(internes) ? internes : [] });
      dispatch({ type: ACTIONS.SET_PRESTATAIRES, payload: Array.isArray(prestData) ? prestData : [] });
      dispatch({ type: ACTIONS.SET_CONSULTANTS, payload: Array.isArray(consultData) ? consultData : [] });
      setTempsData(Array.isArray(temps) ? temps : []);
      setChargesData(Array.isArray(charges) ? charges : []);

      // Map consultant ID -> nom
      const cMap = {};
      (Array.isArray(consultData) ? consultData : []).forEach(c => {
        cMap[c.id] = c.fields?.Consultant || c.fields?.Nom || c.id;
      });

      // Config consultants par année
      const configByYear = {};
      (Array.isArray(configData) ? configData : []).forEach(config => {
        configByYear[config.annee] = {
          nombreConsultants: config.nombreConsultants,
          consultantIds: config.consultantIds || [],
          consultantNames: (config.consultantIds || []).map(id => cMap[id]).filter(Boolean)
        };
      });
      setConsultantConfig(configByYear);

      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
    } catch (err) {
      console.error('Erreur chargement facturation:', err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Erreur lors du chargement des données.' });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [dispatch, ACTIONS]);

  // Chargement initial
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
  }, [loadData]);

  /**
   * Map prestataires par ID
   */
  const prestatairesMap = useMemo(() => {
    const map = {};
    prestataires.forEach(p => {
      map[p.id] = {
        nom: p.fields?.Nom || p.fields?.['Nom du prestataire'] || '-',
        email: p.fields?.Email || p.fields?.['Email'] || ''
      };
    });
    return map;
  }, [prestataires]);

  /**
   * Map consultants par ID
   */
  const consultantsMap = useMemo(() => {
    const map = {};
    consultants.forEach(c => {
      map[c.id] = {
        nom: c.fields?.Nom || '-',
        gdFolderId: c.fields?.GDFolderID || null,
        dropboxFolder: c.fields?.DropboxFolder || null,
        qontoEinvoicingEnabled: !!c.fields?.QontoEinvoicingEnabled
      };
    });
    return map;
  }, [consultants]);

  /**
   * Années disponibles
   */
  const anneesDisponibles = useMemo(() => {
    return getAvailableYearsFromTemps(tempsData);
  }, [tempsData]);

  /**
   * Temps filtrés par année
   */
  const tempsFiltres = useMemo(() => {
    if (!filterAnnee || !tempsData.length) return [];
    return tempsData.filter(t => t.fields?.Année === filterAnnee);
  }, [tempsData, filterAnnee]);

  /**
   * Charges filtrées et enrichies
   */
  const chargesFiltrees = useMemo(() => {
    if (!filterAnnee || !chargesData.length) return [];
    return chargesData
      .filter(c => c.annee === filterAnnee)
      .map(c => ({
        ...c,
        consultantNom: c.consultant ? (consultantsMap[c.consultant]?.nom || null) : null
      }));
  }, [chargesData, filterAnnee, consultantsMap]);

  /**
   * CA net après quote-part (factures internes)
   */
  const { caTotal: caNetTotal } = useRefacturationTotal(tempsFiltres, chargesFiltrees, consultantConfig, filterAnnee);

  /**
   * CA brut (factures externes)
   */
  const { caTotal: caBrut } = useCACalculations(tempsFiltres);

  /**
   * Total facturé interne
   */
  const totalFactureInterne = useMemo(() => {
    if (!facturesInternes || !filterAnnee) return 0;
    return facturesInternes
      .filter(f => f.fields?.Année === filterAnnee)
      .reduce((sum, f) => sum + (f.fields?.['Montant total (HT)'] || 0), 0);
  }, [facturesInternes, filterAnnee]);

  /**
   * Total facturé externe
   */
  const totalFactureExterne = useMemo(() => {
    if (!facturesExternes || !filterAnnee) return 0;
    return facturesExternes
      .filter(f => f.fields?.Année === filterAnnee)
      .reduce((sum, f) => sum + (f.fields?.['Montant total (HT)'] || 0), 0);
  }, [facturesExternes, filterAnnee]);

  /**
   * Données détaillées pour la modale de comparaison
   */
  const facturationDetailData = useMemo(() => {
    const round2 = (v) => Math.round(v * 100) / 100;

    if (activeTab === 'interne') {
      // Facturé par trimestre
      const factureByQ = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
      if (facturesInternes && filterAnnee) {
        facturesInternes
          .filter(f => f.fields?.Année === filterAnnee)
          .forEach(f => {
            const q = f.fields?.Trimestre;
            if (q && factureByQ[q] !== undefined) {
              factureByQ[q] += f.fields?.['Montant total (HT)'] || 0;
            }
          });
      }

      const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
      const periods = quarters.map(q => ({
        label: q,
        ca: caNetTotal?.[q] || 0,
        facture: factureByQ[q],
        reste: round2((caNetTotal?.[q] || 0) - factureByQ[q])
      }));

      return {
        periods,
        totals: {
          ca: caNetTotal?.ANNUEL || 0,
          facture: totalFactureInterne,
          reste: round2((caNetTotal?.ANNUEL || 0) - totalFactureInterne)
        }
      };
    } else {
      // CA par mois
      const caByMonth = {};
      for (let m = 1; m <= 12; m++) caByMonth[m] = 0;

      tempsFiltres.forEach(t => {
        const month = t.fields?.Mois;
        if (month) {
          caByMonth[month] += (t.fields?.['Nombre de jours'] || 0) * (t.fields?.['Taux journalier (€)'] || 0);
        }
      });

      // Facturé par mois
      const factureByMonth = {};
      for (let m = 1; m <= 12; m++) factureByMonth[m] = 0;

      if (facturesExternes && filterAnnee) {
        facturesExternes
          .filter(f => f.fields?.Année === filterAnnee)
          .forEach(f => {
            const m = f.fields?.Mois;
            if (m && factureByMonth[m] !== undefined) {
              factureByMonth[m] += f.fields?.['Montant total (HT)'] || 0;
            }
          });
      }

      const months = Array.from({ length: 12 }, (_, i) => i + 1);
      const periods = months.map(m => ({
        label: new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'long' }).replace(/^./, c => c.toUpperCase()),
        ca: caByMonth[m],
        facture: factureByMonth[m],
        reste: round2(caByMonth[m] - factureByMonth[m])
      }));

      const totalCA = Object.values(caByMonth).reduce((s, v) => s + v, 0);
      return {
        periods,
        totals: {
          ca: totalCA,
          facture: totalFactureExterne,
          reste: round2(totalCA - totalFactureExterne)
        }
      };
    }
  }, [activeTab, facturesInternes, facturesExternes, filterAnnee, caNetTotal, totalFactureInterne, totalFactureExterne, tempsFiltres]);

  /**
   * Prestataires disponibles (pour filtres)
   */
  const prestatairesDisponibles = useMemo(() => {
    const prestIds = new Set();
    facturesExternes.forEach(f => {
      const prestId = f.fields?.['Prestataire porteur']?.[0];
      if (prestId) prestIds.add(prestId);
    });
    return Array.from(prestIds)
      .map(id => ({ id, nom: prestatairesMap[id]?.nom || '-' }))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [facturesExternes, prestatairesMap]);

  /**
   * Consultants disponibles (pour filtres)
   */
  const consultantsDisponibles = useMemo(() => {
    const consultIds = new Set();
    facturesInternes.forEach(f => {
      const consultId = f.fields?.Consultant?.[0];
      if (consultId) consultIds.add(consultId);
    });
    return Array.from(consultIds)
      .map(id => ({ id, nom: consultantsMap[id]?.nom || '-' }))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [facturesInternes, consultantsMap]);

  /**
   * Factures filtrées et triées
   */
  const facturesFiltrees = useMemo(() => {
    const factures = activeTab === 'externe' ? facturesExternes : facturesInternes;

    // Filtrer
    const filtered = filterFactures(factures, {
      hidePaid,
      annee: filterAnnee,
      mois: filterMois,
      trimestre: filterTrimestre,
      prestataire: filterPrestataire,
      consultant: filterConsultant,
      statut: filterStatut
    }, activeTab);

    // Trier
    return sortFactures(filtered, sortColumn, sortDirection, activeTab, {
      prestataires: prestatairesMap,
      consultants: consultantsMap
    });
  }, [
    facturesExternes,
    facturesInternes,
    activeTab,
    filterAnnee,
    filterMois,
    filterTrimestre,
    filterStatut,
    filterPrestataire,
    filterConsultant,
    hidePaid,
    sortColumn,
    sortDirection,
    prestatairesMap,
    consultantsMap
  ]);

  return {
    // Données
    tempsData,
    chargesData,
    consultantConfig,
    prestatairesMap,
    consultantsMap,
    anneesDisponibles,
    tempsFiltres,
    chargesFiltrees,
    // Calculs
    caNetTotal,
    caBrut,
    totalFactureInterne,
    totalFactureExterne,
    facturationDetailData,
    // Filtres
    prestatairesDisponibles,
    consultantsDisponibles,
    facturesFiltrees,
    // Actions
    loadData
  };
}
